import {
	DataSecurityTct,
	SecurityIssuedIn,
	TransactionTitle,
	TransactionType,
} from '@dealstack/shared/lib/generated';
import { isSecurityIssuedInUnits } from '@dealstack/frontend/shared/cap.ts';
import type { UserSubmittableTransactionType } from '@dealstack/shared/lib/common/zod/domain/transaction/transactions.zod';

const HIDE_SELLER_TYPES = [
	TransactionType.SHAREHOLDER_CONTRIBUTION,
	TransactionType.ISSUE,
	TransactionType.CONVERSION_SHARE_CAPITAL,
];

const HIDE_BUYER_TYPES = [
	TransactionType.REDUCTION_IN_SHARE_CAPITAL,
	TransactionType.DIVIDEND,
	TransactionType.CONVERSION_SHARE_CAPITAL,
	TransactionType.REDEMPTION,
	TransactionType.CANCELLATION,
];

const HIDE_BUYER_MIP_TYPES = [
	TransactionType.SHAREHOLDER_CONTRIBUTION,
	TransactionType.REDUCTION_IN_SHARE_CAPITAL,
	TransactionType.DIVIDEND,
	TransactionType.CONVERSION_SHARE_CAPITAL,
	TransactionType.REDEMPTION,
	TransactionType.CANCELLATION,
];

const HIDE_SELLER_MIP_TYPES = [
	TransactionType.SHAREHOLDER_CONTRIBUTION,
	TransactionType.REDUCTION_IN_SHARE_CAPITAL,
	TransactionType.DIVIDEND,
	TransactionType.CONVERSION_SHARE_CAPITAL,
];

export const HIDE_UNITS_TYPES = [
	TransactionType.SHAREHOLDER_CONTRIBUTION,
	TransactionType.REDUCTION_IN_SHARE_CAPITAL,
	TransactionType.DIVIDEND,
	TransactionType.CONVERSION_SHARE_CAPITAL,
];

export const getTransactionCostLabel = (transactionType?: TransactionType | null) => {
	if (
		transactionType === TransactionType.SHAREHOLDER_CONTRIBUTION ||
		transactionType === TransactionType.REDUCTION_IN_SHARE_CAPITAL ||
		transactionType === TransactionType.CONVERSION_SHARE_CAPITAL
	) {
		return 'Capital Amount';
	}
	if (transactionType === TransactionType.DIVIDEND) {
		return 'Dividend Amount';
	}
	return null;
};

/**
 * CAP-1975: an ISSUE qualifies for the legal/beneficial (nominee) split UI only when the security
 * is flagged as a nominee structure and is issued in units. Other transaction types and non-nominee
 * securities keep the standard single-buyer flow.
 *
 * Accepts the zod form value (`UserSubmittableTransactionType`) as well as the generated enum so the
 * create-transaction schema can gate its nominee validation on the same predicate that renders the
 * inputs — see `createNewTransaction.zod.ts`.
 */
export const isNomineeIssueSplitEligible = (
	transactionType: TransactionType | UserSubmittableTransactionType | null | undefined,
	security?: DataSecurityTct
): boolean =>
	transactionType === TransactionType.ISSUE &&
	!!security?.hasNomineeStructure &&
	isSecurityIssuedInUnits(security?.issuedIn);

/**
 * CAP-1975: a TRANSFER on a nominee-structure security (issued in units) lets the user choose which
 * interest is moved — Beneficial, Legal, or Both. This is how a holder who was issued "Both" hands
 * on only the legal title (or only the beneficial interest) of some or all of their units.
 */
export const isNomineeTransferInterestEligible = (
	transactionType: TransactionType | null | undefined,
	security?: DataSecurityTct
): boolean =>
	transactionType === TransactionType.TRANSFER &&
	!!security?.hasNomineeStructure &&
	isSecurityIssuedInUnits(security?.issuedIn);

/**
 * CAP-1976: the interest an existing TRANSFER, REDEMPTION or CANCELLATION carries is an editable
 * dropdown (Beneficial / Legal / Both) on any nominee-structure security issued in units. It applies
 * to single-title rows too — a split leg can be retitled like anything else, by deliberate choice:
 * a register that ends up inconsistent is accepted rather than refused at the edit.
 *
 * The grid does report that inconsistency. OVERSOLD is the OR of two signals with different reach:
 * `source_balance < 0` is the signed arithmetic position, which flags for as long as the position
 * stays negative; `source_oversold` is the fold's own verdict on one row, set when it could not
 * satisfy the retire and left the rail intact. Both are live on a title-split deal — CAP-2038 made
 * the dual scalar the rail's block total minus the shortfall an unsatisfiable retire books, so the
 * arithmetic signal reaches negative there too. The fold verdict is only meaningful when the
 * balances wrapper is resolved WITH a deal id — the events drilldown passes NULL and so still
 * under-reports.
 *
 * CAP-2035: ISSUE is included. It used to be excluded on the grounds that retitling an ISSUE would
 * mint onto mismatched serials, but the fold co-numbers any ISSUE leg carrying an interestPairId
 * onto its sibling's range, and the split writes both legs under one pair — so a split ISSUE lands
 * on ONE serial range, exactly like the paired issue.
 */
export const isNomineeInterestEditable = (
	transactionType: TransactionType | null | undefined,
	security?: DataSecurityTct
): boolean =>
	(transactionType === TransactionType.ISSUE ||
		transactionType === TransactionType.TRANSFER ||
		transactionType === TransactionType.REDEMPTION ||
		transactionType === TransactionType.CANCELLATION) &&
	!!security?.hasNomineeStructure &&
	isSecurityIssuedInUnits(security?.issuedIn);

/**
 * CAP-2033: a REDEMPTION or CANCELLATION on a nominee-structure security (issued in units) lets the
 * user choose which interest is retired. A single-interest choice routes through the inseparable
 * redemption, which retires the counterpart title wherever it sits; "Both" retires the two rails on
 * one row. The rails describe the same shares, so neither can be retired on its own.
 */
export const isNomineeRedemptionEligible = (
	transactionType: TransactionType | null | undefined,
	security?: DataSecurityTct
): boolean =>
	(transactionType === TransactionType.REDEMPTION ||
		transactionType === TransactionType.CANCELLATION) &&
	!!security?.hasNomineeStructure &&
	isSecurityIssuedInUnits(security?.issuedIn);

/**
 * CAP-2011 / CAP-2035 "Update interest transferred": a Both-titled ISSUE / TRANSFER / REDEMPTION /
 * CANCELLATION on a nominee-structure security (issued in units) can be split into its two
 * single-interest legs. Single-title transactions have nothing to split — to move one to another
 * interest, edit its Interest directly (see isNomineeInterestEditable).
 */
export const isSplitInterestEligible = (
	transactionType: TransactionType | null | undefined,
	title: TransactionTitle | null | undefined,
	security?: DataSecurityTct
): boolean =>
	(transactionType === TransactionType.ISSUE ||
		transactionType === TransactionType.TRANSFER ||
		transactionType === TransactionType.REDEMPTION ||
		transactionType === TransactionType.CANCELLATION) &&
	(!title || title === TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE) &&
	!!security?.hasNomineeStructure &&
	isSecurityIssuedInUnits(security?.issuedIn);

/**
 * CAP-1540: whether the holds-for control (sold for / bought for) belongs on the form.
 *
 * It follows the party's own PIV relation, not whether the party row renders — a management loan
 * issued by a PIV hides its seller (an issue's seller is always the security issuer) yet still
 * records the natural person the vehicle holds for, and that field was unreachable.
 *
 * Where the party IS the security issuer, though, the user did not choose it, and only a management
 * loan's issuer is a holding vehicle: a company minting shares holds for nobody, and offering the
 * control there put a value on forms that never send it (the paired nominee issue writes its own
 * payload). So on those types the control is a management loan's alone.
 *
 * A value already stored keeps the control, even once the relation behind it is gone or the security
 * is one that would not offer it, so the attribution stays visible and can be cleared rather than
 * silently disappearing.
 */
export const shouldShowHoldsFor = ({
	hideHoldsFor,
	partyIsSecurityIssuer,
	isManagementLoan,
	storedHoldsForId,
}: {
	hideHoldsFor: boolean;
	partyIsSecurityIssuer: boolean;
	isManagementLoan: boolean;
	storedHoldsForId?: string | null;
}): boolean => {
	if (storedHoldsForId) {
		return true;
	}

	if (hideHoldsFor) {
		return false;
	}

	return !partyIsSecurityIssuer || isManagementLoan;
};

export const useTransactionFieldsVisibility = (
	transactionType?: TransactionType | null,
	selectedSecurityIssuedIn?: SecurityIssuedIn
) => {
	const type = transactionType as TransactionType;
	const issuedInUnits = isSecurityIssuedInUnits(selectedSecurityIssuedIn);

	const hideSeller = HIDE_SELLER_TYPES.includes(type);
	const hideBuyer = HIDE_BUYER_TYPES.includes(type);
	const hideBuyerMip = HIDE_BUYER_MIP_TYPES.includes(type);
	const hideSellerMip =
		transactionType === TransactionType.ISSUE || HIDE_SELLER_MIP_TYPES.includes(type);
	const hideUnits = !issuedInUnits || HIDE_UNITS_TYPES.includes(type);
	const hideCost = transactionType === TransactionType.CANCELLATION;

	return {
		hideSeller,
		hideBuyer,
		hideBuyerMip,
		hideSellerMip,
		hideUnits,
		hideCost,
	};
};
